/**
 * Returns true when the given URL should be preserved as a post-login return target.
 *
 * Two cases qualify:
 *   1. An authenticated session (NAME token present) — preserves the page across session-timeout redirects.
 *   2. An unauthenticated deep-link recipient (?view=) — so the recipient lands on the
 *      correct saved view after completing login.
 */
export function shouldStoreReturnUrl(returnUrl: string): boolean
{
    if (!returnUrl) return false;

    const hasDeepLink = returnUrl.includes("view=");
    const userId      = localStorage.getItem("NAME");
    return !!(userId || hasDeepLink);
}

/**
 * Returns true when a stored return context belongs to the user who just logged in
 * and is therefore safe to consume as a redirect target.
 *
 * Three cases allow consumption:
 *   - ctx.userId is empty — the context was stored for an unauthenticated deep-link
 *     recipient; skip the user-match check entirely.
 *   - ctx.userId matches the logged-in user ID.
 *   - ctx.userId matches the user name — SSO paths store user.name rather than user.id.
 *
 * A non-empty userId that does not match either value is rejected to prevent
 * cross-user redirect.
 */
export function shouldAcceptReturnContext(
    ctx: { userId?: string; returnUrl?: string },
    loggedInUserId: string,
    loggedInUserName?: string): boolean
{
    if (!ctx?.returnUrl) return false;

    return !ctx.userId
           || ctx.userId === loggedInUserId
           || (!!loggedInUserName && ctx.userId === loggedInUserName);
}
